import { useCallback, useEffect, useState } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import { ShippingMethod } from 'types/entity/cart';
import { Order } from 'types/entity/order';

type UseOrderInfoDataProps = {
  order?: Order;
  shippingMethods: ShippingMethod[];
};

const useOrderInfoData = ({ order, shippingMethods }: UseOrderInfoDataProps) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const [orderNumber, setOrderNumber] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');

  const updateOrderNumber = useCallback(() => {
    const label = order?.orderNumber ?? '';
    setOrderNumber(label);
  }, [order?.orderNumber]);

  const updateDeliveryMethod = useCallback(() => {
    const shippingMethod = shippingMethods?.find(
      (method) => method.shippingMethodId === order?.shippingInfo?.shippingMethodId,
    );

    setDeliveryMethod(
      shippingMethod?.name ??
        (order?.custom?.fields?.shippyProPickupPointId
          ? formatMessage({ id: 'shipping.pickupPoint', defaultMessage: 'Punto di ritiro' })
          : ''),
    );
  }, [order?.shippingInfo, shippingMethods]);

  const updateShippingAddress = useCallback(() => {
    if (order?.custom?.fields?.shippyProPickupPointId) {
      const parts = [order.custom.fields.shippyProPickupPointAddress ?? ''];
      if (order.custom.fields.shippyProPickupPointCourier) {
        parts.push(order.custom.fields.shippyProPickupPointCourier);
      }
      setShippingAddress(parts.join('\n'));
    } else if (order?.shippingAddress) {
      const { streetName, streetNumber, city, postalCode, province } = order.shippingAddress;
      const street = [streetName, streetNumber].filter((x) => !!x).join(' ');
      const cityLine = [`${postalCode} ${city}`.trim(), province && `(${province})`].filter((x) => !!x).join(' ');
      setShippingAddress(`${street}, ${cityLine}`);
    }
  }, [order?.shippingAddress, order?.custom]);

  useEffect(() => {
    if (order) {
      updateOrderNumber();
      updateDeliveryMethod();
      updateShippingAddress();
    }
  }, [order, updateDeliveryMethod, updateOrderNumber, updateShippingAddress]);

  return {
    orderNumber,
    deliveryMethod,
    shippingAddress,
  };
};

export default useOrderInfoData;
